import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface SpecRow {
  values: string[];
}

interface SpecTable {
  headers: string[];
  rows: SpecRow[];
  notes: string[];
  standard: string;
}

const specData: Record<string, SpecTable> = {
  seamless: {
    standard: 'ASTM A106 Gr.B / A53 / KS D3562',
    headers: ['호칭경(DN)', '외경(mm)', 'SCH 40\n두께(mm)', 'SCH 80\n두께(mm)', 'SCH 160\n두께(mm)', 'SCH 40\n중량(kg/m)', 'SCH 80\n중량(kg/m)', '표준길이'],
    rows: [
      { values: ['DN 25', '33.4', '3.38', '4.55', '6.35', '2.50', '3.24', '6m / 12m'] },
      { values: ['DN 40', '48.3', '3.68', '5.08', '7.14', '4.05', '5.41', '6m / 12m'] },
      { values: ['DN 50', '60.3', '3.91', '5.54', '8.74', '5.44', '7.48', '6m / 12m'] },
      { values: ['DN 65', '73.0', '5.16', '7.01', '9.53', '8.63', '11.41', '6m / 12m'] },
      { values: ['DN 80', '88.9', '5.49', '7.62', '11.13', '11.29', '15.27', '6m / 12m'] },
      { values: ['DN 100', '114.3', '6.02', '8.56', '13.49', '16.07', '22.32', '6m / 12m'] },
      { values: ['DN 150', '168.3', '7.11', '10.97', '18.26', '28.26', '42.56', '6m / 12m'] },
      { values: ['DN 200', '219.1', '8.18', '12.70', '23.01', '42.55', '64.64', '6m / 12m'] },
      { values: ['DN 250', '273.1', '9.27', '12.70', '28.58', '60.31', '81.55', '6m / 12m'] },
      { values: ['DN 300', '323.9', '9.53', '12.70', '33.32', '73.88', '97.46', '6m / 12m'] },
      { values: ['DN 350', '355.6', '9.53', '12.70', '35.71', '81.33', '107.39', '6m / 12m'] },
      { values: ['DN 400', '406.4', '9.53', '12.70', '36.53', '93.00', '123.30', '6m / 12m'] },
    ],
    notes: [
      '심레스(무계목)관은 용접부가 없어 고온·고압 환경에서 균일한 내압 성능을 발휘합니다.',
      'SCH 160 및 XXH(Double Extra Heavy) 두께도 공급 가능합니다.',
      '화학성분 분석서(MTR) 및 비파괴검사(UT) 성적서 제공 가능합니다.',
    ],
  },
  pressure: {
    standard: 'KS D3562 / ASTM A106 / ASME B36.10',
    headers: ['호칭경(DN)', '외경(mm)', 'SCH 40\n두께(mm)', 'SCH 80\n두께(mm)', 'SCH 120\n두께(mm)', 'SCH 160\n두께(mm)', 'SCH 80\n중량(kg/m)', '표준길이'],
    rows: [
      { values: ['DN 15', '21.3', '2.77', '3.73', '—', '4.78', '1.62', '6m / 12m'] },
      { values: ['DN 20', '26.7', '2.87', '3.91', '—', '5.56', '2.20', '6m / 12m'] },
      { values: ['DN 25', '33.4', '3.38', '4.55', '—', '6.35', '3.24', '6m / 12m'] },
      { values: ['DN 40', '48.3', '3.68', '5.08', '—', '7.14', '5.41', '6m / 12m'] },
      { values: ['DN 50', '60.3', '3.91', '5.54', '8.74', '8.74', '7.48', '6m / 12m'] },
      { values: ['DN 80', '88.9', '5.49', '7.62', '11.13', '11.13', '15.27', '6m / 12m'] },
      { values: ['DN 100', '114.3', '6.02', '8.56', '11.13', '13.49', '22.32', '6m / 12m'] },
      { values: ['DN 150', '168.3', '7.11', '10.97', '14.27', '18.26', '42.56', '6m / 12m'] },
      { values: ['DN 200', '219.1', '8.18', '12.70', '18.26', '23.01', '64.64', '6m / 12m'] },
      { values: ['DN 250', '273.1', '9.27', '12.70', '19.05', '28.58', '81.55', '6m / 12m'] },
      { values: ['DN 300', '323.9', '9.53', '12.70', '25.40', '33.32', '97.46', '6m / 12m'] },
      { values: ['DN 350', '355.6', '9.53', '12.70', '—', '35.71', '107.39', '6m / 12m'] },
    ],
    notes: [
      '압력배관용 강관은 수압시험(Hydrostatic Test) 후 출고됩니다.',
      'KS D3562 SPPS 380 / 420 두 가지 강도 등급 모두 공급 가능합니다.',
      '비파괴검사(UT, RT) 및 화학성분 분석서(MTR) 제공 가능합니다.',
    ],
  },
  general: {
    standard: 'KS D3507 / KS D3595 / KS D3562',
    headers: ['호칭경(DN)', '외경(mm)', '두께(mm)', '단위중량(kg/m)', '내경(mm)', '최고사용압력(MPa)', '용도', '표준길이'],
    rows: [
      { values: ['DN 15', '21.7', '2.0', '0.96', '17.7', '0.6', '일반배관', '5.5m / 6m'] },
      { values: ['DN 20', '27.2', '2.0', '1.22', '23.2', '0.6', '일반배관', '5.5m / 6m'] },
      { values: ['DN 25', '34.0', '2.3', '1.73', '29.4', '0.6', '일반배관', '5.5m / 6m'] },
      { values: ['DN 32', '42.7', '2.3', '2.19', '38.1', '0.6', '일반배관', '5.5m / 6m'] },
      { values: ['DN 40', '48.6', '2.3', '2.50', '44.0', '0.6', '일반배관', '5.5m / 6m'] },
      { values: ['DN 50', '60.5', '2.3', '3.13', '55.9', '0.6', '일반배관', '5.5m / 6m'] },
      { values: ['DN 65', '76.3', '2.3', '3.98', '71.7', '0.6', '일반배관', '5.5m / 6m'] },
      { values: ['DN 80', '89.1', '2.8', '5.74', '83.5', '0.6', '일반배관', '5.5m / 6m'] },
      { values: ['DN 100', '114.3', '3.2', '8.46', '107.9', '0.6', '일반배관', '5.5m / 6m'] },
      { values: ['DN 125', '139.8', '3.5', '11.36', '132.8', '0.6', '일반배관', '5.5m / 6m'] },
      { values: ['DN 150', '165.2', '4.0', '15.44', '157.2', '0.6', '일반배관', '5.5m / 6m'] },
      { values: ['DN 200', '216.3', '4.5', '22.96', '207.3', '0.6', '일반배관', '5.5m / 6m'] },
    ],
    notes: [
      'KS D3507: 배관용 탄소강 강관 / KS D3595: 일반 구조용 탄소강 강관',
      '외면 방청 도장(광명단 또는 에나멜) 처리 후 출고됩니다.',
      'DN 250 이상 대구경 및 특수 두께는 사전 협의가 필요합니다.',
    ],
  },
  structural: {
    standard: 'KS D3566 / ASTM A500',
    headers: ['구분', '외경/단면(mm)', '두께(mm)', '단위중량(kg/m)', '단면적(cm²)', '단면2차모멘트(cm⁴)', '회전반경(cm)', '표준길이'],
    rows: [
      { values: ['원형', 'φ76.3', '3.2 / 4.5 / 6.0', '5.77 ~ 10.68', '7.35 ~ 13.60', '43.0 ~ 71.9', '2.42 ~ 2.30', '12m'] },
      { values: ['원형', 'φ101.6', '3.2 / 4.5 / 6.0', '7.77 ~ 14.21', '9.90 ~ 18.10', '100.9 ~ 167.7', '3.19 ~ 3.04', '12m'] },
      { values: ['원형', 'φ139.8', '4.5 / 6.0 / 9.0', '14.95 ~ 29.00', '19.04 ~ 36.95', '282.6 ~ 497.7', '3.85 ~ 3.67', '12m'] },
      { values: ['원형', 'φ165.2', '5.0 / 7.0 / 9.0', '19.73 ~ 34.77', '25.13 ~ 44.27', '524.8 ~ 870.5', '4.57 ~ 4.43', '12m'] },
      { values: ['원형', 'φ216.3', '6.0 / 8.0 / 12.0', '30.97 ~ 61.88', '39.45 ~ 78.83', '1,233 ~ 2,228', '5.59 ~ 5.32', '12m'] },
      { values: ['각형', '100×100', '3.2 / 4.5 / 6.0', '9.56 ~ 17.38', '12.18 ~ 22.14', '148.6 ~ 249.5', '3.49 ~ 3.36', '12m'] },
      { values: ['각형', '150×150', '4.5 / 6.0 / 9.0', '20.31 ~ 37.31', '25.88 ~ 47.51', '632.8 ~ 1,087', '4.94 ~ 4.78', '12m'] },
      { values: ['각형', '200×200', '6.0 / 9.0 / 12.0', '35.57 ~ 68.47', '45.30 ~ 87.24', '2,002 ~ 3,553', '6.65 ~ 6.38', '12m'] },
      { values: ['각형', '250×250', '6.0 / 9.0 / 12.0', '44.97 ~ 87.34', '57.30 ~ 111.2', '3,980 ~ 7,126', '8.34 ~ 8.01', '12m'] },
      { values: ['직사각형', '100×50', '3.2 / 4.5', '6.56 ~ 9.01', '8.36 ~ 11.48', '85.8 ~ 112.1', '3.20 ~ 3.12', '12m'] },
      { values: ['직사각형', '200×100', '4.5 / 6.0', '18.64 ~ 24.31', '23.74 ~ 30.95', '791.4 ~ 1,007', '5.78 ~ 5.70', '12m'] },
    ],
    notes: [
      '두께 옵션이 여럿인 경우 최소~최대 중량을 범위로 표기했습니다.',
      '특수 단면(타원형, 이형관 등)은 별도 문의해주세요.',
      '건축구조용 강관의 항복강도: 355MPa 이상 (KS D3566 SHK400W)',
    ],
  },
  stainless: {
    standard: 'ASTM A312 / KS D3576',
    headers: ['호칭경(DN)', '외경(mm)', 'SCH 10S\n두께(mm)', 'SCH 40S\n두께(mm)', 'SCH 80S\n두께(mm)', 'SCH 10S\n중량(kg/m)', 'SCH 40S\n중량(kg/m)', '표준길이'],
    rows: [
      { values: ['DN 15', '21.3', '1.65', '2.77', '3.73', '0.80', '1.27', '6m'] },
      { values: ['DN 20', '26.7', '1.65', '2.87', '3.91', '1.02', '1.69', '6m'] },
      { values: ['DN 25', '33.4', '1.65', '3.38', '4.55', '1.30', '2.50', '6m'] },
      { values: ['DN 40', '48.3', '1.65', '3.68', '5.08', '1.91', '4.05', '6m'] },
      { values: ['DN 50', '60.3', '1.65', '3.91', '5.54', '2.41', '5.44', '6m'] },
      { values: ['DN 80', '88.9', '2.11', '5.49', '7.62', '4.52', '11.29', '6m'] },
      { values: ['DN 100', '114.3', '2.11', '6.02', '8.56', '5.85', '16.07', '6m'] },
      { values: ['DN 150', '168.3', '2.77', '7.11', '10.97', '11.39', '28.26', '6m'] },
      { values: ['DN 200', '219.1', '3.76', '8.18', '12.70', '20.11', '42.55', '6m'] },
      { values: ['DN 250', '273.1', '4.19', '9.27', '12.70', '27.79', '60.31', '6m'] },
      { values: ['DN 300', '323.9', '4.57', '9.53', '12.70', '36.17', '73.88', '6m'] },
    ],
    notes: [
      '재질: SUS304 (기본) / SUS304L / SUS316 / SUS316L (요청 시 공급)',
      '표면 마감: AP (에니얼링·피클링), EP (전해연마) 선택 가능',
      '식품·의약품 배관 사용 시 BA 마감 또는 EP 마감을 권장합니다.',
    ],
  },
  alloy: {
    standard: 'ASTM A335 / A213 / A369',
    headers: ['호칭경(DN)', '외경(mm)', '두께(mm)', '단위중량(kg/m)', '재질 등급', '최고사용온도(℃)', '최고사용압력(MPa)', '표준길이'],
    rows: [
      { values: ['DN 15', '21.3', '2.77 ~ 4.78', '1.27 ~ 2.01', 'P5/P9/P11/P22/P91', '550 ~ 650', '10 ~ 25', '6m / 12m'] },
      { values: ['DN 25', '33.4', '3.38 ~ 6.35', '2.50 ~ 4.35', 'P5/P9/P11/P22/P91', '550 ~ 650', '10 ~ 25', '6m / 12m'] },
      { values: ['DN 50', '60.3', '3.91 ~ 9.53', '5.44 ~ 12.22', 'P5/P9/P11/P22/P91', '550 ~ 650', '10 ~ 25', '6m / 12m'] },
      { values: ['DN 80', '88.9', '5.49 ~ 15.24', '11.29 ~ 28.00', 'P5/P9/P11/P22/P91', '550 ~ 650', '10 ~ 25', '6m / 12m'] },
      { values: ['DN 100', '114.3', '6.02 ~ 17.12', '16.07 ~ 42.12', 'P5/P9/P11/P22/P91', '550 ~ 650', '10 ~ 25', '6m / 12m'] },
      { values: ['DN 150', '168.3', '7.11 ~ 21.95', '28.26 ~ 79.73', 'P5/P9/P11/P22/P91', '550 ~ 650', '10 ~ 25', '6m / 12m'] },
      { values: ['DN 200', '219.1', '8.18 ~ 22.23', '42.55 ~ 105.5', 'P11/P22/P91', '550 ~ 650', '10 ~ 22', '6m / 12m'] },
      { values: ['DN 250', '273.1', '9.27 ~ 25.40', '60.31 ~ 148.9', 'P11/P22/P91', '550 ~ 650', '10 ~ 22', '6m / 12m'] },
      { values: ['DN 300', '323.9', '9.53 ~ 25.40', '73.88 ~ 178.2', 'P22/P91', '600 ~ 650', '10 ~ 20', '6m / 12m'] },
    ],
    notes: [
      'P91 (9Cr-1Mo-V): 장시간 크리프 강도가 우수하여 초고온·고압 플랜트에 사용됩니다.',
      '모든 합금강관은 열처리(노말라이징+템퍼링) 후 출고됩니다.',
      '화학성분 분석서(MTR) 및 비파괴검사(UT/RT) 성적서 제공 가능합니다.',
    ],
  },
  fitting: {
    standard: 'ASME B16.9 / KS B1541',
    headers: ['호칭경', '구분', '두께 기준', '압력등급', '재질 옵션', '마감', '표준'],
    rows: [
      { values: ['1/2" ~ 2"', '엘보 (90°/45°)', 'SCH 40 / 80', '150# ~ 3000#', 'CS / SS / Alloy', 'BW / SW / THD', 'ASME B16.9'] },
      { values: ['2-1/2" ~ 24"', '엘보 (90°/45°)', 'SCH 20~160, STD/XH', '150# ~ 600#', 'CS / SS / Alloy', 'BW', 'ASME B16.9'] },
      { values: ['1/2" ~ 24"', '티 (동경/이경)', 'SCH 40 / 80 / 160', '150# ~ 2500#', 'CS / SS / Alloy', 'BW / SW', 'ASME B16.9'] },
      { values: ['1/2" ~ 24"', '리듀서 (동심/편심)', 'SCH 40 / 80', '150# ~ 600#', 'CS / SS / Alloy', 'BW', 'ASME B16.9'] },
      { values: ['1/2" ~ 24"', '캡 (Cap)', 'SCH 40 / 80 / 160', '150# ~ 1500#', 'CS / SS / Alloy', 'BW', 'ASME B16.9'] },
      { values: ['1/2" ~ 4"', '소켓 (Socket)', 'SCH 40 / 80 / 160', '150# ~ 3000#', 'CS / SS', 'SW / THD', 'ASME B16.11'] },
      { values: ['1/2" ~ 24"', '크로스 (Cross)', 'SCH 40 / 80', '150# ~ 600#', 'CS / SS', 'BW', 'ASME B16.9'] },
      { values: ['1/2" ~ 4"', '커플링 / 유니온', 'SCH 40 / 80', '150# ~ 3000#', 'CS / SS', 'THD', 'ASME B16.11'] },
    ],
    notes: [
      'BW: 맞대기 용접 / SW: 소켓 용접 / THD: 나사식',
      'CS(탄소강): A234 WPB / SS(스테인리스): A403 WP304/316',
      '두께, 재질, 압력등급 맞춤 제작 및 RTJ·RF 면가공 가능',
    ],
  },
  flange: {
    standard: 'ASME B16.5 / KS B1503',
    headers: ['호칭경(DN)', '압력등급(#)', '외경(mm)', '볼트홀 수×크기', '플랜지 두께(mm)', '중량(kg)', '형식', '표준'],
    rows: [
      { values: ['DN 50 (2")', '150#', '152', '4 × φ15.7', '16.0', '2.0', 'WN / SO / BL', 'ASME B16.5'] },
      { values: ['DN 50 (2")', '300#', '165', '8 × φ15.7', '22.4', '3.2', 'WN / SO / BL', 'ASME B16.5'] },
      { values: ['DN 80 (3")', '150#', '190', '4 × φ19.0', '19.1', '3.9', 'WN / SO / BL', 'ASME B16.5'] },
      { values: ['DN 100 (4")', '150#', '229', '8 × φ19.0', '20.6', '6.1', 'WN / SO / BL', 'ASME B16.5'] },
      { values: ['DN 100 (4")', '300#', '254', '8 × φ22.4', '28.4', '9.5', 'WN / SO / BL', 'ASME B16.5'] },
      { values: ['DN 150 (6")', '150#', '279', '8 × φ22.4', '22.4', '10.2', 'WN / SO / BL', 'ASME B16.5'] },
      { values: ['DN 200 (8")', '150#', '343', '8 × φ22.4', '25.4', '18.0', 'WN / SO / BL', 'ASME B16.5'] },
      { values: ['DN 250 (10")', '150#', '406', '12 × φ25.4', '27.0', '28.6', 'WN / SO / BL', 'ASME B16.5'] },
      { values: ['DN 300 (12")', '150#', '483', '12 × φ25.4', '28.4', '40.8', 'WN / SO / BL', 'ASME B16.5'] },
      { values: ['DN 300 (12")', '300#', '520', '16 × φ31.8', '38.1', '68.0', 'WN / SO / BL', 'ASME B16.5'] },
      { values: ['DN 400 (16")', '150#', '597', '16 × φ28.6', '31.8', '68.5', 'WN / SO / BL', 'ASME B16.5'] },
      { values: ['DN 500 (20")', '150#', '737', '20 × φ28.6', '35.1', '120.2', 'WN / SO / BL', 'ASME B16.5'] },
    ],
    notes: [
      'WN: 용접형(Weld Neck) / SO: 슬립온(Slip On) / BL: 블라인드(Blind)',
      '재질: A105(탄소강) / A182 F304/316(스테인리스) / A182 F11/F22(합금강)',
      '압력등급 600# ~ 2500# 및 RTJ 그루브 가공 제품도 공급 가능',
    ],
  },
  pile: {
    standard: 'KS F 4602 / KS D3565',
    headers: ['외경(mm)', '두께(mm)', '단위중량(kg/m)', '단면적(cm²)', '단면2차모멘트(cm⁴)', '항복강도(MPa)', '최대 근입장(m)', '표준길이'],
    rows: [
      { values: ['φ300', '9.0 / 12.0', '64.7 ~ 85.4', '82.4 ~ 108.9', '8,630 ~ 11,041', '≥ 315', '30 ~ 50', '12m / 15m'] },
      { values: ['φ350', '9.0 / 12.0', '75.7 ~ 100.1', '96.4 ~ 127.4', '13,653 ~ 17,512', '≥ 315', '30 ~ 50', '12m / 15m'] },
      { values: ['φ400', '9.0 / 12.0 / 16.0', '86.7 ~ 154.0', '110.5 ~ 196.2', '20,630 ~ 34,540', '≥ 315', '40 ~ 60', '12m / 15m'] },
      { values: ['φ450', '9.0 / 12.0 / 16.0', '97.8 ~ 173.4', '124.5 ~ 220.9', '29,617 ~ 49,560', '≥ 315', '40 ~ 60', '12m / 15m'] },
      { values: ['φ500', '9.0 / 12.0 / 16.0', '108.8 ~ 192.8', '138.6 ~ 245.6', '40,876 ~ 68,258', '≥ 315', '40 ~ 60', '12m / 15m'] },
      { values: ['φ600', '9.0 / 12.0 / 16.0', '131.0 ~ 231.5', '166.7 ~ 294.9', '70,784 ~ 118,120', '≥ 315', '50 ~ 70', '12m / 15m'] },
      { values: ['φ700', '12.0 / 16.0 / 19.0', '203.0 ~ 319.4', '258.7 ~ 406.8', '179,900 ~ 272,400', '≥ 355', '50 ~ 70', '12m / 15m'] },
      { values: ['φ800', '12.0 / 16.0 / 19.0', '232.1 ~ 365.2', '295.6 ~ 465.1', '271,600 ~ 412,200', '≥ 355', '50 ~ 70', '12m / 15m'] },
      { values: ['φ900', '12.0 / 16.0 / 19.0', '261.1 ~ 411.0', '332.5 ~ 523.3', '387,600 ~ 591,400', '≥ 355', '60 ~ 80', '12m / 15m'] },
      { values: ['φ1000', '16.0 / 19.0 / 22.0', '394.4 ~ 537.3', '502.6 ~ 684.5', '600,800 ~ 899,600', '≥ 355', '60 ~ 80', '12m / 15m'] },
      { values: ['φ1200', '16.0 / 19.0 / 22.0', '474.9 ~ 645.3', '604.9 ~ 821.8', '1,042,000 ~ 1,543,000', '≥ 355', '60 ~ 80', '12m / 15m'] },
    ],
    notes: [
      '이음 방법: 현장 용접 이음 또는 기계식 이음 선택 가능합니다.',
      '도장 처리: 외면 방청 도장 기본 / 해상 환경 시 에폭시 도장을 추천합니다.',
      'φ1000 이상 대구경 파일은 제작 기간 및 운반 조건 사전 협의가 필요합니다.',
    ],
  },
  'pe-coating': {
    standard: 'KS D3589 / DIN 30670 / EN 10290',
    headers: ['파이프 외경(mm)', '코팅 종류', '코팅 총 두께(mm)', '코팅 구성', '최고 사용온도(℃)', '최저 사용온도(℃)', '내전압(V/mm)', '적용규격'],
    rows: [
      { values: ['114 ~ 219', '2LPE', '1.8 ~ 2.5', '에폭시+PE', '50', '-40', '≥ 25,000', 'DIN 30670'] },
      { values: ['114 ~ 219', '3LPE', '2.0 ~ 3.0', '에폭시+접착층+PE', '60', '-40', '≥ 25,000', 'DIN 30670'] },
      { values: ['219 ~ 508', '2LPE', '2.0 ~ 2.5', '에폭시+PE', '50', '-40', '≥ 25,000', 'DIN 30670'] },
      { values: ['219 ~ 508', '3LPE', '2.2 ~ 3.2', '에폭시+접착층+PE', '60', '-40', '≥ 25,000', 'DIN 30670'] },
      { values: ['508 ~ 813', '3LPE', '2.5 ~ 3.5', '에폭시+접착층+PE', '60', '-40', '≥ 25,000', 'DIN 30670'] },
      { values: ['813 ~ 1219', '3LPE', '3.0 ~ 4.0', '에폭시+접착층+PE', '60', '-40', '≥ 25,000', 'DIN 30670'] },
      { values: ['114 ~ 813', '3LPP', '2.5 ~ 4.5', '에폭시+접착층+PP', '90', '-10', '≥ 25,000', 'EN 10290'] },
      { values: ['114 ~ 508', 'FBE 에폭시', '0.4 ~ 0.6', 'FBE 에폭시', '80', '-30', '≥ 30,000', 'KS D3589'] },
    ],
    notes: [
      '3LPP(폴리프로필렌) 코팅은 고온 배관 및 해저 배관에 권장됩니다.',
      'FBE(Fusion Bonded Epoxy) 코팅은 내부 코팅에도 적용 가능합니다.',
      '코팅 홀리데이(핀홀) 검사 및 접착력 시험 성적서 제공 가능합니다.',
    ],
  },
};

interface SpecModalProps {
  productId: string;
  productName: string;
  onClose: () => void;
}

export default function SpecModal({ productId, productName, onClose }: SpecModalProps) {
  const { t } = useTranslation();
  const spec = specData[productId];

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKey);
    };
  }, [onClose]);

  if (!spec) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-2 md:p-8"
      style={{ backgroundColor: 'rgba(0,0,0,0.65)' }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-6xl max-h-[94vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between px-4 md:px-8 py-4 md:py-5 bg-[#1C2E4A] flex-shrink-0">
          <div className="flex items-center gap-3 md:gap-4 min-w-0">
            <div className="w-7 h-7 md:w-8 md:h-8 flex items-center justify-center shrink-0">
              <i className="ri-file-list-3-line text-[#E8762B] text-lg md:text-xl"></i>
            </div>
            <div className="min-w-0">
              <p className="text-[#E8762B] text-[10px] md:text-xs font-semibold tracking-widest">{t('spec_modal_subtitle')}</p>
              <h3 className="text-white text-base md:text-xl font-black truncate" style={{ fontFamily: "'Noto Serif KR', serif" }}>
                {productName} {t('spec_modal_title_suffix')}
              </h3>
            </div>
          </div>
          <div className="flex items-center gap-2 md:gap-3 shrink-0 ml-3">
            <span className="hidden lg:block bg-white/10 text-white/70 text-xs px-3 py-1.5 rounded-full font-mono">
              {spec.standard}
            </span>
            <button
              onClick={onClose}
              className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-white text-lg"></i>
            </button>
          </div>
        </div>

        {/* Table Area */}
        <div className="flex-1 overflow-auto p-3 md:p-6">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-[#F4F6F8]">
                  {spec.headers.map((h, i) => (
                    <th
                      key={i}
                      className="px-2 md:px-4 py-2 md:py-3 text-left text-[10px] md:text-xs font-bold text-[#1C2E4A] border border-[#E5E9F0] whitespace-pre-line leading-tight"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {spec.rows.map((row, ri) => (
                  <tr
                    key={ri}
                    className={`${ri % 2 === 0 ? 'bg-white' : 'bg-[#FAFBFC]'} hover:bg-[#FFF5EE] transition-colors`}
                  >
                    {row.values.map((val, ci) => (
                      <td
                        key={ci}
                        className={`px-2 md:px-4 py-2 md:py-2.5 border border-[#E5E9F0] text-[#374151] font-mono text-[10px] md:text-xs ${
                          ci === 0 ? 'font-bold text-[#1C2E4A] font-sans' : ''
                        }`}
                      >
                        {val}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Notes */}
          <div className="mt-4 md:mt-6 bg-[#FFF8F3] border border-[#FDDEC0] rounded-xl p-4 md:p-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-information-line text-[#E8762B] text-base"></i>
              </div>
              <span className="text-xs font-bold text-[#E8762B] tracking-wide">{t('spec_notes_label')}</span>
            </div>
            <ul className="space-y-1.5">
              {spec.notes.map((_note, i) => (
                <li key={i} className="flex items-start gap-2 text-xs text-[#6B5844]">
                  <span className="flex-shrink-0 w-4 h-4 mt-0.5 flex items-center justify-center bg-[#E8762B]/20 rounded-full text-[#E8762B] font-bold text-[10px]">
                    {i + 1}
                  </span>
                  <span className="leading-relaxed">{t(`spec_note_${productId}_${i}`)}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 px-4 md:px-8 py-3 md:py-4 border-t border-[#E5E9F0] bg-[#FAFBFC] flex-shrink-0">
          <p className="text-xs text-[#9CA3AF]">
            {t('spec_footer_note')}
          </p>
          <div className="flex items-center gap-3">
            <a
              href="#contact"
              onClick={(e) => {
                e.preventDefault();
                onClose();
                setTimeout(() => {
                  document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
                }, 150);
              }}
              className="flex items-center gap-2 bg-[#E8762B] hover:bg-[#D4661F] text-white px-4 md:px-5 py-2 rounded-md text-xs font-bold transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-mail-send-line"></i>
              {t('spec_custom_btn')}
            </a>
            <button
              onClick={onClose}
              className="px-4 md:px-5 py-2 rounded-md text-xs font-semibold text-[#6B7280] border border-[#E5E9F0] hover:bg-[#F4F6F8] transition-colors cursor-pointer whitespace-nowrap"
            >
              {t('spec_close_btn')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
